import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import EquipoSelect from "@/components/EquipoSelect";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, ArrowRight, Save, Loader2, Camera, Image, Send, AlertTriangle, X, Plus, Trash2, Armchair, Thermometer } from "lucide-react";
import { toast } from "sonner";
import ConsumoRepuestos from "@/components/ConsumoRepuestos";
import RepuestoCombobox from "@/components/RepuestoCombobox";

import { logoColor as LOGO_URL } from "@/lib/logos";
const STEPS = ["Datos Generales", "Checklist", "Cierre", "Revisión"];
const ESTADO_OPS = [
  { value: "operativo", label: "Operativo" },
  { value: "operativo_con_observaciones", label: "Operativo con observaciones" },
  { value: "fuera_de_servicio", label: "Fuera de servicio" },
];

export default function MantencionPreventiva() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api, user, isAdmin } = useAuth();
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [readOnly, setReadOnly] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [reportId, setReportId] = useState(id || null);
  const [folio, setFolio] = useState("");
  const [activeCat, setActiveCat] = useState(0);
  const [photoTarget, setPhotoTarget] = useState(null);
  const [estado, setEstado] = useState("borrador");
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const lastSnapshotRef = useRef("");
  const stateRef = useRef({ datos: null, checklist: null, cierre: null });
  const reportIdRef = useRef(reportId);
  const inFlightSaveRef = useRef(null);  // serializa los PUT para evitar carreras
  useEffect(() => { reportIdRef.current = reportId; }, [reportId]);

  const [clients, setClients] = useState([]);
  const [catalogo, setCatalogo] = useState([]);
  const [datos, setDatos] = useState({
    fecha: new Date().toISOString().split("T")[0],
    hora_inicio: new Date().toTimeString().slice(0, 5),
    hora_termino: "",
    cliente_id: "",
    cliente: "",
    sucursal: "",
    direccion: "",
    contacto_cliente: "",
    correo_cliente: "",
    telefono_cliente: "",
    marca_sillon: "",
    modelo: "",
    numero_serie: "",
    numero_box: "",
    codigo_interno: "",
    estado_inicial: "",
    equipo_tipo: "",
  });
  const [checklist, setChecklist] = useState([]);
  const [cierre, setCierre] = useState({
    diagnostico_general: "",
    trabajo_realizado: "",
    recomendaciones: "",
    repuestos_sugeridos: "",
    estado_final: "",
    firma_tecnico: user?.nombre || "",
    firma_cliente: "",
    repuestos_utilizados: [],
    repuestos_sugeridos_imagenes: [],
  });

  useEffect(() => {
    api.get("/api/clients").then((r) => setClients(r.data)).catch(() => {});
    api.get("/api/repuestos").then((r) => setCatalogo(r.data)).catch(() => {});
  }, [api]);

  useEffect(() => {
    if (id) {
      api.get(`/api/maintenance/${id}`)
        .then((r) => {
          const d = r.data;
          const loadedCierre = {
            diagnostico_general: "",
            trabajo_realizado: "",
            recomendaciones: "",
            repuestos_sugeridos: "",
            estado_final: "",
            firma_tecnico: user?.nombre || "",
            firma_cliente: "",
            ...(d.cierre || {}),
            repuestos_utilizados: (d.cierre || {}).repuestos_utilizados || [],
            repuestos_sugeridos_imagenes: (d.cierre || {}).repuestos_sugeridos_imagenes || [],
          };
          setDatos(d.datos_generales);
          setChecklist(d.checklist || []);
          setCierre(loadedCierre);
          setFolio(d.folio);
          setReportId(d.id);
          setEstado(d.estado || "borrador");
          // Sembrar el snapshot para que el auto-save no dispare un PUT
          // inmediato con datos idénticos a los recién cargados.
          lastSnapshotRef.current = JSON.stringify({
            datos: d.datos_generales, checklist: d.checklist || [], cierre: loadedCierre,
          });
          if (d.estado === "completado" && user?.cargo !== "admin") {
            setReadOnly(true);
          }
        })
        .catch(() => toast.error("Error al cargar reporte"));
    }
    // MP nueva: el checklist se carga recién cuando el técnico elige el tipo
    // de equipo (Unidad Dental / Autoclave) — ver elegirTipo().
  }, [id, api, user?.cargo]);

  // Selecciona el tipo de equipo y carga el checklist que le corresponde.
  const elegirTipo = async (tipo) => {
    try {
      const r = await api.get(`/api/maintenance/template?equipo=${tipo}`);
      setChecklist(
        r.data.map((cat) => ({
          categoria: cat.categoria,
          items: cat.items.map((item) =>
            item.tipo === "numero"
              ? { ...item, valor: "", observacion: "", imagenes: [] }
              : {
                  ...item,
                  resultado: item.default_resultado || "conforme",
                  observacion: "",
                  imagenes: [],
                }
          ),
        }))
      );
      setActiveCat(0);
      setDatos((p) => ({ ...p, equipo_tipo: tipo }));
    } catch {
      toast.error("No se pudo cargar el checklist del equipo");
    }
  };

  const cambiarTipo = () => {
    const ok = window.confirm(
      "Cambiar el tipo de equipo reinicia el checklist (se pierde lo marcado). ¿Continuar?"
    );
    if (!ok) return;
    setChecklist([]);
    setDatos((p) => ({ ...p, equipo_tipo: "" }));
    setStep(0);
  };

  // MP nueva sin tipo elegido → primero se selecciona el equipo
  const necesitaTipo = !id && !datos.equipo_tipo;

  const ud = (f, v) => setDatos((p) => ({ ...p, [f]: v }));
  const uc = (f, v) => setCierre((p) => ({ ...p, [f]: v }));

  // Repuestos utilizados (catálogo + otros) en el cierre
  const repuestosCatalogo = (cierre.repuestos_utilizados || []).filter((r) => !r.es_otro);

  const setRepuestos = (next) => uc("repuestos_utilizados", next);

  const addRepuestoCatalogoRow = () =>
    setRepuestos([...(cierre.repuestos_utilizados || []), { sku: "", nombre: "", cantidad: 1, es_otro: false }]);

  const updateRepuesto = (idx, field, value) => {
    const list = [...(cierre.repuestos_utilizados || [])];
    list[idx] = { ...list[idx], [field]: value };
    setRepuestos(list);
  };

  const updateRepuestoCatalogo = (idx, repId) => {
    const rep = catalogo.find((r) => r.id === repId);
    if (!rep) return;
    const list = [...(cierre.repuestos_utilizados || [])];
    list[idx] = { ...list[idx], sku: rep.sku, nombre: rep.nombre, es_otro: false };
    setRepuestos(list);
  };

  const removeRepuesto = (idx) => {
    const list = [...(cierre.repuestos_utilizados || [])];
    list.splice(idx, 1);
    setRepuestos(list);
  };

  const addRepuestoOtroRow = () => {
    const ok = window.confirm(
      "¿Estás seguro que el repuesto no se encuentra en el catálogo?\n\n" +
      "Aceptar: agregar repuesto como texto libre.\n" +
      "Cancelar: volver a revisar el catálogo."
    );
    if (!ok) return;
    setRepuestos([...(cierre.repuestos_utilizados || []), { sku: "OTROS", nombre: "", cantidad: 1, es_otro: true }]);
  };

  const setItem = (ci, ii, f, v) => {
    setChecklist((p) => {
      const n = JSON.parse(JSON.stringify(p));
      n[ci].items[ii][f] = v;
      return n;
    });
  };

  const handleResultado = (ci, ii, value) => {
    if (value === "no_conforme") {
      const ok = window.confirm(
        "¿Estás seguro que es no conformidad? Deberá agregar los respaldos fotográficos correspondientes."
      );
      if (!ok) return;
    }
    setItem(ci, ii, "resultado", value);
  };

  const handleClientSelect = (clientId) => {
    const c = clients.find((x) => x.id === clientId);
    if (!c) return;
    ud("cliente_id", clientId);
    ud("cliente", c.nombre);
    ud("contacto_cliente", c.contacto_nombre || "");
    ud("correo_cliente", c.contacto_email || "");
    ud("telefono_cliente", c.contacto_telefono || "");
    ud("sucursal", "");
    ud("direccion", "");
  };

  const selectedClient = clients.find((c) => c.id === datos.cliente_id);

  const allItems = checklist.flatMap((c) => c.items);
  const answered = allItems.filter((i) =>
    i.tipo === "numero" ? String(i.valor || "").trim() : i.resultado
  ).length;
  const pct = allItems.length > 0 ? Math.round((answered / allItems.length) * 100) : 0;
  const ncs = allItems.filter((i) => i.resultado === "no_conforme");

  const saveDraft = async ({ silent = false } = {}) => {
    if (readOnly) return reportId;
    // Si ya hay un save en vuelo, espéralo antes de lanzar otro: serializa los
    // PUT para que un auto-save tardío no sobreescriba datos más nuevos.
    if (inFlightSaveRef.current) {
      try { await inFlightSaveRef.current; } catch (_) {}
    }
    const p = (async () => {
      setSaving(true);
      try {
        if (!reportId) {
          const r = await api.post("/api/maintenance", {
            tipo: "preventiva",
            datos_generales: datos,
          });
          const newId = r.data.id;
          setReportId(newId);
          setFolio(r.data.folio);
          if (!silent) toast.success(`Borrador creado: ${r.data.folio}`);
          setLastSavedAt(Date.now());
          lastSnapshotRef.current = JSON.stringify({ datos, checklist, cierre });
          return newId;
        }
        await api.put(`/api/maintenance/${reportId}`, {
          datos_generales: datos,
          checklist,
          cierre,
        });
        if (!silent) toast.success("Borrador guardado");
        setLastSavedAt(Date.now());
        lastSnapshotRef.current = JSON.stringify({ datos, checklist, cierre });
        return reportId;
      } catch {
        if (!silent) toast.error("Error al guardar");
        return null;
      } finally {
        setSaving(false);
      }
    })();
    inFlightSaveRef.current = p;
    try { return await p; }
    finally { if (inFlightSaveRef.current === p) inFlightSaveRef.current = null; }
  };

  // Auto-guardado: persistir borrador automáticamente cuando cambian datos
  // generales, checklist o cierre. Debounce de 2 s para no martillar la red.
  // Solo se activa si ya existe un reportId, o si se llenó cliente_id (para
  // no crear borradores vacíos al abrir el formulario).
  useEffect(() => {
    stateRef.current = { datos, checklist, cierre };
  }, [datos, checklist, cierre]);

  useEffect(() => {
    if (readOnly || saving || completing) return;
    if (!reportId && !datos.cliente_id) return;
    const snapshot = JSON.stringify({ datos, checklist, cierre });
    if (snapshot === lastSnapshotRef.current) return;
    const t = setTimeout(() => {
      saveDraft({ silent: true });
    }, 2000);
    return () => clearTimeout(t);
  }, [datos, checklist, cierre, reportId, readOnly, saving, completing]); // eslint-disable-line react-hooks/exhaustive-deps

  // Flush sincrónico al cerrar la pestaña: usa sendBeacon o keepalive.
  useEffect(() => {
    const onBeforeUnload = () => {
      const rid = reportIdRef.current;
      const { datos: d, checklist: cl, cierre: ci } = stateRef.current;
      if (!rid || !d) return;
      const snap = JSON.stringify({ datos: d, checklist: cl, cierre: ci });
      if (snap === lastSnapshotRef.current) return;
      try {
        const url = `${api.defaults.baseURL || ""}/api/maintenance/${rid}`;
        const token = localStorage.getItem("odf_token");
        const body = JSON.stringify({ datos_generales: d, checklist: cl, cierre: ci });
        // fetch keepalive sobrevive al cierre de la pestaña.
        fetch(url, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body,
          keepalive: true,
        }).catch(() => {});
      } catch (_) { /* best effort */ }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [api]);

  const goToStep = async (nextStep) => {
    if (!readOnly) {
      const savedId = await saveDraft({ silent: true });
      if (!savedId && !reportId) return;
    }
    setStep(nextStep);
  };

  const requiredMissing = () => {
    const missing = [];
    if (!datos.fecha) missing.push("Fecha");
    if (!datos.cliente) missing.push("Cliente");
    if (!datos.sucursal) missing.push("Sucursal");
    if (!datos.marca_sillon) missing.push("Marca");
    if (!datos.modelo) missing.push("Modelo");
    if (!datos.numero_serie) missing.push("N° Serie");
    if (!datos.numero_box) missing.push("N° Box");
    if (!datos.estado_inicial) missing.push("Estado inicial");
    // Campos numéricos del checklist (PPM, Voltaje, etc.) y fotos de no conformidad
    checklist.forEach((cat) => {
      cat.items.forEach((it) => {
        if (it.tipo === "numero" && it.obligatorio && !String(it.valor || "").trim()) {
          missing.push(`${it.descripcion} (${cat.categoria})`);
        }
        // Las fotos de respaldo NO son obligatorias para el admin
        if (!isAdmin && it.resultado === "no_conforme" && (it.imagenes || []).length === 0) {
          missing.push(`Foto de no conformidad: ${it.descripcion}`);
        }
      });
    });
    // Cierre
    if (!cierre.diagnostico_general) missing.push("Diagnóstico general");
    if (!cierre.trabajo_realizado) missing.push("Trabajo realizado");
    if (!cierre.estado_final) missing.push("Estado final");
    // Si se sugieren repuestos, debe haber al menos 1 imagen de respaldo
    // (no obligatorio para el admin)
    if (
      !isAdmin &&
      String(cierre.repuestos_sugeridos || "").trim() &&
      (cierre.repuestos_sugeridos_imagenes || []).length === 0
    ) {
      missing.push("Imagen de respaldo para Repuestos sugeridos");
    }
    return missing;
  };

  const completeReport = async () => {
    if (readOnly) return;

    const missing = requiredMissing();
    if (missing.length > 0) {
      toast.error(`Faltan campos obligatorios: ${missing.join(", ")}`);
      // Saltar al paso donde está el primer faltante
      const firstMissing = missing[0];
      if (
        firstMissing.startsWith("Foto de no conformidad") ||
        firstMissing.includes("(")
      ) {
        setStep(1); // Checklist
      } else if (
        ["Diagnóstico general", "Trabajo realizado", "Estado final"].includes(firstMissing) ||
        firstMissing.startsWith("Imagen de respaldo")
      ) {
        setStep(2); // Cierre
      } else {
        setStep(0); // Datos generales
      }
      return;
    }

    setCompleting(true);
    try {
      // Espera cualquier auto-save en vuelo antes de hacer el PUT final, así
      // un save tardío no sobreescribe los datos recién regenerados.
      if (inFlightSaveRef.current) {
        try { await inFlightSaveRef.current; } catch (_) {}
      }

      let currentId = reportId;

      if (!currentId) {
        currentId = await saveDraft({ silent: true });
      }

      if (!currentId) {
        throw new Error("No se pudo guardar el borrador");
      }

      await api.put(`/api/maintenance/${currentId}`, {
        datos_generales: datos,
        checklist,
        cierre,
      });
      // Marca este PUT como referencia para que el autosave compare contra él
      // (evita un re-PUT inmediato con los mismos datos).
      lastSnapshotRef.current = JSON.stringify({ datos, checklist, cierre });

      const r = await api.post(`/api/maintenance/${currentId}/complete`);
      const isResend = r.data.reenvio;
      toast.success(isResend
        ? `Reporte ${r.data.folio} actualizado y reenviado`
        : `Reporte ${r.data.folio} completado`
      );

      if (r.data.correo_enviado) {
        toast.success("Correo enviado");
      } else if (r.data.email_error) {
        toast.error(`Correo no enviado: ${r.data.email_error}`);
      }

      navigate("/tecnico/dashboard");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Error al completar");
    } finally {
      setCompleting(false);
    }
  };

  const MAX_IMAGES = 6;        // límite de fotos "normales" del checklist
  const MAX_IMAGES_SUGERIDOS = 2;

  // Las fotos de ítems "no conforme" NO cuentan para el límite de 6.
  const regularImages = checklist.reduce(
    (acc, c) =>
      acc +
      c.items.reduce(
        (a, it) => a + (it.resultado === "no_conforme" ? 0 : (it.imagenes?.length || 0)),
        0
      ),
    0
  );

  const uploadImagen = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const url = reportId
      ? `/api/maintenance/images/upload?report_id=${reportId}`
      : "/api/maintenance/images/upload";
    const r = await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
    return r.data.id;
  };

  const handleFileChange = async (e) => {
    // Permite adjuntar VARIAS fotos de una vez (galería con selección múltiple);
    // la cámara entrega una. Se respeta el máximo de cada sección.
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0 || !photoTarget) return;

    const validas = [];
    for (const f of files) {
      if (!f.type.startsWith("image/")) { toast.error(`${f.name}: solo se permiten imágenes`); continue; }
      if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name}: máximo 10MB`); continue; }
      validas.push(f);
    }
    if (validas.length === 0) return;

    const subir = async (lote) => {
      const ids = [];
      let errores = 0;
      for (const f of lote) {
        try { ids.push(await uploadImagen(f)); }
        catch (err) {
          errores += 1;
          toast.error(err.response?.data?.detail || `Error al subir ${f.name}`);
        }
      }
      if (ids.length) toast.success(ids.length === 1 ? "Imagen subida" : `${ids.length} imágenes subidas`);
      return { ids, errores };
    };

    // Imágenes de respaldo de "Repuestos sugeridos" (máx 2)
    if (photoTarget.tipo === "repuesto_sugerido") {
      const actuales = cierre.repuestos_sugeridos_imagenes || [];
      const cupo = MAX_IMAGES_SUGERIDOS - actuales.length;
      if (cupo <= 0) {
        toast.error(`Máximo ${MAX_IMAGES_SUGERIDOS} imágenes para repuestos sugeridos.`);
        return;
      }
      if (validas.length > cupo) {
        toast.info(`Solo se subirán ${cupo} (máximo ${MAX_IMAGES_SUGERIDOS} en esta sección).`);
      }
      const { ids } = await subir(validas.slice(0, cupo));
      if (ids.length) uc("repuestos_sugeridos_imagenes", [...actuales, ...ids]);
      return;
    }

    // Imágenes del checklist
    const { ci, ii } = photoTarget;
    const targetItem = checklist[ci]?.items[ii];
    const isNoConforme = targetItem?.resultado === "no_conforme";
    // El límite de 6 solo aplica a fotos que NO son de una no conformidad
    let lote = validas;
    if (!isNoConforme) {
      const cupo = MAX_IMAGES - regularImages;
      if (cupo <= 0) {
        toast.error(`Se ha alcanzado el máximo de ${MAX_IMAGES} imágenes en el checklist.`);
        return;
      }
      if (validas.length > cupo) {
        toast.info(`Solo se subirán ${cupo} (quedan ${cupo} de ${MAX_IMAGES} del checklist).`);
        lote = validas.slice(0, cupo);
      }
    }
    const { ids } = await subir(lote);
    if (ids.length) setItem(ci, ii, "imagenes", [...(checklist[ci].items[ii].imagenes || []), ...ids]);
  };

  const triggerCamera = (target) => {
    setPhotoTarget(target);
    setTimeout(() => cameraRef.current?.click(), 50);
  };
  const triggerGallery = (target) => {
    setPhotoTarget(target);
    setTimeout(() => galleryRef.current?.click(), 50);
  };

  const deleteImage = async (ci, ii, imgId) => {
    try {
      await api.delete(`/api/maintenance/images/${imgId}`);
      setItem(
        ci,
        ii,
        "imagenes",
        checklist[ci].items[ii].imagenes.filter((id) => id !== imgId)
      );
      toast.success("Imagen eliminada");
    } catch {
      toast.error("Error al eliminar imagen");
    }
  };

  const deleteRepuestoSugeridoImagen = async (imgId) => {
    try {
      await api.delete(`/api/maintenance/images/${imgId}`);
      uc(
        "repuestos_sugeridos_imagenes",
        (cierre.repuestos_sugeridos_imagenes || []).filter((id) => id !== imgId)
      );
      toast.success("Imagen eliminada");
    } catch {
      toast.error("Error al eliminar imagen");
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleFileChange}
      />
      <input
        ref={galleryRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={handleFileChange}
      />

      <header className="bg-white border-b sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="" className="h-7" />
            <span className="text-sm font-semibold text-[#384091]">
              {folio || "Nueva MP"}
            </span>
          </div>

          <div className="flex items-center gap-2">
            {!readOnly && (
              <span className="hidden sm:inline-block text-[11px] text-gray-500 mr-1">
                {saving
                  ? "Guardando…"
                  : lastSavedAt
                    ? `Guardado ${new Date(lastSavedAt).toLocaleTimeString("es-CL", { hour: "2-digit", minute: "2-digit" })}`
                    : "Auto-guardado activo"}
              </span>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => saveDraft()}
              disabled={saving || readOnly || completing}
            >
              {saving ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
              ) : (
                <Save className="h-3.5 w-3.5 mr-1" />
              )}
              Guardar
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/tecnico/dashboard")}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {!necesitaTipo && (
      <div className="bg-white border-b px-4 py-3">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center justify-between mb-2">
            {STEPS.map((s, i) => (
              <button
                key={i}
                onClick={() => goToStep(i)}
                disabled={saving || completing}
                className={`text-xs font-medium px-3 py-1 rounded-full transition-all ${
                  step === i
                    ? "bg-[#0FB4B7] text-white"
                    : step > i
                      ? "bg-emerald-50 text-emerald-700"
                      : "text-gray-400 bg-gray-50"
                } ${saving || completing ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                {s}
              </button>
            ))}
          </div>
          <Progress value={((step + 1) / STEPS.length) * 100} className="h-1.5" />
        </div>
      </div>
      )}

      <main className="max-w-3xl mx-auto px-4 py-6">
        {necesitaTipo && (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6 space-y-4">
              <h2 className="font-bold text-lg text-[#384091]">¿Qué equipo vas a mantener?</h2>
              <p className="text-sm text-gray-500 -mt-2">
                Según el equipo que elijas se abre el checklist que corresponde.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => elegirTipo("unidad_dental")}
                  className="rounded-2xl border-2 border-gray-200 hover:border-[#0FB4B7] bg-white p-6 text-left transition-colors"
                >
                  <Armchair className="h-7 w-7 text-[#0FB4B7] mb-2" />
                  <p className="font-semibold text-gray-800">Unidad Dental</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Sillón, unidad dental, unidad de agua, reflector y accesorios
                  </p>
                </button>
                <button
                  type="button"
                  onClick={() => elegirTipo("autoclave")}
                  className="rounded-2xl border-2 border-gray-200 hover:border-[#E0653A] bg-white p-6 text-left transition-colors"
                >
                  <Thermometer className="h-7 w-7 text-[#E0653A] mb-2" />
                  <p className="font-semibold text-gray-800">Autoclave</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Cámara, estanques de agua, válvulas y ciclos de prueba
                  </p>
                </button>
              </div>
            </CardContent>
          </Card>
        )}
        {readOnly && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm text-amber-700 font-medium">
            🔒 Reporte completado — Solo lectura. Un administrador puede editarlo.
          </div>
        )}
        {!readOnly && estado === "completado" && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 text-sm text-blue-700 font-medium">
            ✏️ Editando reporte completado. Al guardar y enviar se regenerará el PDF y se reenviará el correo.
          </div>
        )}

        {!necesitaTipo && step === 0 && (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6 space-y-4">
              <h2 className="font-bold text-lg text-[#384091]">Datos Generales</h2>

              {datos.equipo_tipo && (
                <div className="flex items-center gap-2 -mt-2">
                  <Badge className={`border-0 ${datos.equipo_tipo === "autoclave" ? "bg-[#E0653A]/10 text-[#E0653A]" : "bg-[#0FB4B7]/10 text-[#0D999C]"}`}>
                    {datos.equipo_tipo === "autoclave" ? <Thermometer className="h-3 w-3 mr-1" /> : <Armchair className="h-3 w-3 mr-1" />}
                    {datos.equipo_tipo === "autoclave" ? "Autoclave" : "Unidad Dental"}
                  </Badge>
                  {!id && !readOnly && (
                    <button type="button" className="text-xs text-gray-400 underline" onClick={cambiarTipo}>
                      cambiar equipo
                    </button>
                  )}
                </div>
              )}

              <div>
                <Label>Fecha *</Label>
                <Input
                  type="date"
                  value={datos.fecha}
                  onChange={(e) => ud("fecha", e.target.value)}
                  className="mt-1 h-11"
                />
              </div>

              <div className="border-t pt-3">
                <Label>Cliente *</Label>
                <Select value={datos.cliente_id} onValueChange={handleClientSelect}>
                  <SelectTrigger className="mt-1 h-11">
                    <SelectValue placeholder="Seleccionar cliente..." />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedClient && selectedClient.direcciones?.length > 0 && (
                <div>
                  <Label>Sucursal *</Label>
                  <Select
                    value={datos.sucursal}
                    onValueChange={(v) => {
                      const d = selectedClient.direcciones.find((x) => x.nombre === v);
                      if (d) {
                        ud("sucursal", d.nombre);
                        ud("direccion", `${d.direccion}, ${d.comuna}`);
                      }
                    }}
                  >
                    <SelectTrigger className="mt-1 h-11">
                      <SelectValue placeholder="Seleccionar dirección..." />
                    </SelectTrigger>
                    <SelectContent>
                      {selectedClient.direcciones.map((d) => (
                        <SelectItem key={d.nombre} value={d.nombre}>
                          {d.nombre} — {d.direccion}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Contacto</Label>
                  <Input
                    value={datos.contacto_cliente}
                    onChange={(e) => ud("contacto_cliente", e.target.value)}
                    className="mt-1 h-11"
                    autoComplete="name"
                  />
                </div>
                <div>
                  <Label>Correo</Label>
                  <Input
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    value={datos.correo_cliente}
                    onChange={(e) => ud("correo_cliente", e.target.value)}
                    className="mt-1 h-11"
                  />
                </div>
              </div>

              <div className="border-t pt-3">
                <h3 className="font-medium text-sm mb-3">Datos del Equipo</h3>
                {isAdmin && datos.cliente_id && (
                  <div className="mb-3">
                    <EquipoSelect
                      clienteId={datos.cliente_id}
                      sucursal={datos.sucursal}
                      onSelect={(e) => setDatos((p) => ({
                        ...p,
                        marca_sillon: e.marca || p.marca_sillon,
                        modelo: e.modelo || p.modelo,
                        numero_serie: e.numero_serie || p.numero_serie,
                        numero_box: e.numero_box || p.numero_box,
                        codigo_interno: e.codigo_interno || p.codigo_interno,
                      }))}
                    />
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label>Marca *</Label>
                    <Input
                      value={datos.marca_sillon}
                      onChange={(e) => ud("marca_sillon", e.target.value)}
                      className="mt-1 h-11"
                    />
                  </div>
                  <div>
                    <Label>Modelo *</Label>
                    <Input
                      value={datos.modelo}
                      onChange={(e) => ud("modelo", e.target.value)}
                      className="mt-1 h-11"
                    />
                  </div>
                  <div>
                    <Label>N° Serie *</Label>
                    <Input
                      value={datos.numero_serie}
                      onChange={(e) => ud("numero_serie", e.target.value)}
                      className="mt-1 h-11"
                    />
                  </div>
                  <div>
                    <Label>Código interno</Label>
                    <Input
                      value={datos.codigo_interno}
                      onChange={(e) => ud("codigo_interno", e.target.value)}
                      className="mt-1 h-11"
                    />
                  </div>
                  <div>
                    <Label>N° Box *</Label>
                    <Input
                      type="number"
                      min="1"
                      inputMode="numeric"
                      placeholder="Solo números"
                      value={datos.numero_box}
                      onChange={(e) => ud("numero_box", e.target.value.replace(/\D/g, ""))}
                      className="mt-1 h-11"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label>Estado inicial *</Label>
                    <Select
                      value={datos.estado_inicial}
                      onValueChange={(v) => ud("estado_inicial", v)}
                    >
                      <SelectTrigger className="mt-1 h-11">
                        <SelectValue placeholder="Seleccionar..." />
                      </SelectTrigger>
                      <SelectContent>
                        {ESTADO_OPS.map((o) => (
                          <SelectItem key={o.value} value={o.value}>
                            {o.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {!necesitaTipo && step === 1 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h2 className="font-bold text-lg text-[#384091]">Checklist</h2>
              <div className="flex items-center gap-2">
                <Badge className="bg-blue-50 text-blue-700 border-0">
                  📷 {regularImages}/{MAX_IMAGES}
                </Badge>
                <Badge className={pct === 100 ? "bg-emerald-50 text-emerald-700 border-0" : "bg-amber-50 text-amber-700 border-0"}>
                  {pct}% ({answered}/{allItems.length})
                </Badge>
              </div>
            </div>

            <div className="flex overflow-x-auto gap-1.5 pb-2 -mx-4 px-4" style={{ scrollbarWidth: "none" }}>
              {checklist.map((c, i) => {
                const done = c.items.filter((x) =>
                  x.tipo === "numero" ? String(x.valor || "").trim() : x.resultado
                ).length;
                return (
                  <button
                    key={i}
                    onClick={() => setActiveCat(i)}
                    className={`flex-shrink-0 text-xs px-3 py-2 rounded-lg font-medium whitespace-nowrap transition-all ${
                      activeCat === i
                        ? "bg-[#0FB4B7] text-white"
                        : done === c.items.length
                          ? "bg-emerald-50 text-emerald-700"
                          : "bg-gray-100 text-gray-500"
                    }`}
                  >
                    {c.categoria} ({done}/{c.items.length})
                  </button>
                );
              })}
            </div>

            <Card className="border-0 shadow-sm">
              <CardContent className="p-4 space-y-3">
                <h3 className="font-semibold text-sm text-[#384091]">{checklist[activeCat]?.categoria}</h3>

                {checklist[activeCat]?.items.map((item, ii) => (
                  <div
                    key={item.id}
                    className={`p-3 rounded-xl border transition-all ${
                      item.tipo === "numero"
                        ? "border-[#0FB4B7]/40 bg-[#0FB4B7]/5"
                        : item.resultado === "no_conforme"
                          ? "border-red-200 bg-red-50/50"
                          : item.resultado === "conforme"
                            ? "border-emerald-200 bg-emerald-50/30"
                            : item.resultado === "no_aplica"
                              ? "border-gray-200 bg-gray-50"
                              : "border-gray-200"
                    }`}
                  >
                    <p className="text-sm text-gray-800 font-medium mb-2">
                      {item.descripcion}
                      {item.obligatorio && <span className="text-red-500 ml-0.5">*</span>}
                    </p>

                    {item.tipo === "numero" ? (
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min="0"
                          inputMode="numeric"
                          placeholder="Solo números"
                          value={item.valor ?? ""}
                          onChange={(e) =>
                            setItem(activeCat, ii, "valor", e.target.value.replace(/\D/g, ""))
                          }
                          className="max-w-[180px] h-11 bg-white"
                          disabled={readOnly}
                        />
                        {item.unidad && (
                          <span className="text-sm text-gray-600 font-medium">{item.unidad}</span>
                        )}
                      </div>
                    ) : (
                      <div className="flex gap-2 flex-wrap">
                        {[
                          { v: "conforme", l: "✓ Conforme", sel: "bg-emerald-50 text-emerald-700 border-emerald-300" },
                          { v: "no_conforme", l: "✗ No Conforme", sel: "bg-red-50 text-red-700 border-red-300" },
                          { v: "no_aplica", l: "— No Aplica", sel: "bg-gray-100 text-gray-600 border-gray-300" },
                        ].map((o) => (
                          <button
                            key={o.v}
                            onClick={() => handleResultado(activeCat, ii, o.v)}
                            className={`text-xs px-3 py-1.5 rounded-lg font-medium border transition-all ${
                              item.resultado === o.v
                                ? `${o.sel} shadow-sm`
                                : "border-gray-200 text-gray-400 hover:bg-gray-50"
                            }`}
                            disabled={readOnly}
                          >
                            {o.l}
                          </button>
                        ))}
                      </div>
                    )}

                    {(item.tipo === "numero" || item.resultado) && (
                      <Input
                        placeholder="Observación (opcional)..."
                        value={item.observacion}
                        onChange={(e) => setItem(activeCat, ii, "observacion", e.target.value)}
                        className="mt-2 h-10"
                        disabled={readOnly}
                      />
                    )}

                    {!isAdmin && item.resultado === "no_conforme" && (item.imagenes || []).length === 0 && (
                      <p className="text-xs text-red-600 mt-2 flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" /> No conformidad: adjunta al menos 1 foto de respaldo.
                      </p>
                    )}

                    <div className="flex items-center gap-3 mt-2">
                      {!readOnly && item.permite_imagenes !== false && (
                        <>
                          <button
                            onClick={() => triggerCamera({ tipo: "checklist", ci: activeCat, ii })}
                            className="text-xs flex items-center gap-1 text-[#0FB4B7] hover:underline font-medium"
                          >
                            <Camera className="h-3 w-3" /> Tomar foto
                          </button>
                          <button
                            onClick={() => triggerGallery({ tipo: "checklist", ci: activeCat, ii })}
                            className="text-xs flex items-center gap-1 text-[#384091] hover:underline font-medium"
                          >
                            <Image className="h-3 w-3" /> Cargar
                          </button>
                        </>
                      )}

                      {item.imagenes?.length > 0 && (
                        <div className="flex gap-1 flex-wrap">
                          {item.imagenes.map((imgId, imgIdx) => (
                            <span
                              key={imgId}
                              className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full flex items-center gap-1"
                            >
                              📷 {imgIdx + 1}
                              {!readOnly && (
                                <button
                                  onClick={() => deleteImage(activeCat, ii, imgId)}
                                  className="text-red-400 hover:text-red-600 ml-0.5"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              )}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                <div className="flex justify-between pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={activeCat === 0}
                    onClick={() => setActiveCat((p) => p - 1)}
                  >
                    <ArrowLeft className="h-4 w-4 mr-1" /> Anterior
                  </Button>
                  <Button
                    size="sm"
                    className="bg-[#0FB4B7] text-white"
                    disabled={activeCat === checklist.length - 1}
                    onClick={() => setActiveCat((p) => p + 1)}
                  >
                    Siguiente <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {!necesitaTipo && step === 2 && (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6 space-y-4">
              <h2 className="font-bold text-lg text-[#384091]">Cierre del Reporte</h2>

              {[
                ["Diagnóstico general *", "diagnostico_general"],
                ["Trabajo realizado *", "trabajo_realizado"],
                ["Recomendaciones", "recomendaciones"],
              ].map(([l, k]) => (
                <div key={k}>
                  <Label>{l}</Label>
                  <Textarea
                    rows={3}
                    value={cierre[k]}
                    onChange={(e) => uc(k, e.target.value)}
                    className="mt-1"
                    disabled={readOnly}
                  />
                </div>
              ))}

              {/* Repuestos sugeridos + imágenes de respaldo */}
              <div>
                <Label>Repuestos sugeridos</Label>
                <Textarea
                  rows={3}
                  value={cierre.repuestos_sugeridos}
                  onChange={(e) => uc("repuestos_sugeridos", e.target.value)}
                  className="mt-1"
                  disabled={readOnly}
                />
                <div className="mt-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs text-gray-500">
                      Imágenes de respaldo ({(cierre.repuestos_sugeridos_imagenes || []).length}/{MAX_IMAGES_SUGERIDOS})
                    </Label>
                  </div>
                  {!isAdmin &&
                    String(cierre.repuestos_sugeridos || "").trim() &&
                    (cierre.repuestos_sugeridos_imagenes || []).length === 0 && (
                      <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" /> Obligatorio: adjunta al menos 1 imagen si indicas repuestos sugeridos.
                      </p>
                    )}
                  <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                    {!readOnly && (cierre.repuestos_sugeridos_imagenes || []).length < MAX_IMAGES_SUGERIDOS && (
                      <>
                        <button
                          type="button"
                          onClick={() => triggerCamera({ tipo: "repuesto_sugerido" })}
                          className="text-xs flex items-center gap-1 text-[#0FB4B7] hover:underline font-medium"
                        >
                          <Camera className="h-3 w-3" /> Tomar foto
                        </button>
                        <button
                          type="button"
                          onClick={() => triggerGallery({ tipo: "repuesto_sugerido" })}
                          className="text-xs flex items-center gap-1 text-[#384091] hover:underline font-medium"
                        >
                          <Image className="h-3 w-3" /> Cargar
                        </button>
                      </>
                    )}
                    {(cierre.repuestos_sugeridos_imagenes || []).map((imgId, idx) => (
                      <div key={imgId} className="relative w-16 h-16 rounded border overflow-hidden">
                        <img
                          src={`${api.defaults.baseURL}/api/maintenance/images/${imgId}`}
                          alt={`Repuesto sugerido ${idx + 1}`}
                          className="w-full h-full object-cover"
                        />
                        {!readOnly && (
                          <button
                            type="button"
                            onClick={() => deleteRepuestoSugeridoImagen(imgId)}
                            className="absolute top-0 right-0 bg-red-500 text-white rounded-bl p-0.5"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* === REPUESTOS UTILIZADOS === */}
              <div className="border-t pt-4 space-y-3">
                <h3 className="font-semibold text-sm text-[#384091]">Repuestos utilizados</h3>

                {/* Catálogo */}
                <div className="space-y-2">
                  <Label className="text-xs text-gray-500">Del catálogo</Label>
                  {repuestosCatalogo.length === 0 && (
                    <p className="text-xs text-gray-400">No se han agregado repuestos del catálogo.</p>
                  )}
                  {(cierre.repuestos_utilizados || []).map((rep, i) => {
                    if (rep.es_otro) return null;
                    return (
                      <div key={i} className="flex gap-2 items-center">
                        <RepuestoCombobox
                          catalogo={catalogo}
                          value={catalogo.find((c) => c.sku === rep.sku)?.id || ""}
                          onSelect={(repId) => updateRepuestoCatalogo(i, repId)}
                          disabled={readOnly}
                          placeholder={rep.nombre || "Buscar repuesto..."}
                        />
                        <Input
                          type="number"
                          min="1"
                          inputMode="numeric"
                          value={rep.cantidad}
                          onChange={(e) => updateRepuesto(i, "cantidad", parseInt(e.target.value) || 1)}
                          className="w-16 bg-white text-center h-11"
                          disabled={readOnly}
                        />
                        {!readOnly && (
                          <Button variant="ghost" size="sm" onClick={() => removeRepuesto(i)} className="h-11 w-11 p-0">
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    );
                  })}
                  {!readOnly && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={addRepuestoCatalogoRow}
                      className="border-dashed border-[#0FB4B7]/40 text-[#0FB4B7] hover:bg-[#0FB4B7]/5"
                    >
                      <Plus className="h-4 w-4 mr-1" /> Agregar repuesto del catálogo
                    </Button>
                  )}
                </div>

                {/* Otros */}
                <div className="space-y-2 pt-2">
                  <Label className="text-xs text-gray-500">Otros repuestos (texto libre)</Label>
                  {(cierre.repuestos_utilizados || []).map((rep, i) => {
                    if (!rep.es_otro) return null;
                    return (
                      <div key={i} className="flex gap-2 items-center">
                        <Input
                          value={rep.nombre}
                          onChange={(e) => updateRepuesto(i, "nombre", e.target.value)}
                          placeholder="Nombre del repuesto..."
                          className="flex-1 bg-white h-11"
                          disabled={readOnly}
                        />
                        <Input
                          type="number"
                          min="1"
                          inputMode="numeric"
                          value={rep.cantidad}
                          onChange={(e) => updateRepuesto(i, "cantidad", parseInt(e.target.value) || 1)}
                          className="w-16 bg-white text-center h-11"
                          disabled={readOnly}
                        />
                        {!readOnly && (
                          <Button variant="ghost" size="sm" onClick={() => removeRepuesto(i)} className="h-11 w-11 p-0">
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    );
                  })}
                  {!readOnly && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={addRepuestoOtroRow}
                      className="border-dashed border-[#384091]/40 text-[#384091] hover:bg-[#384091]/5"
                    >
                      <Plus className="h-4 w-4 mr-1" /> Otros repuestos
                    </Button>
                  )}
                </div>
              </div>

              <div>
                <Label>Estado final *</Label>
                <Select
                  value={cierre.estado_final}
                  onValueChange={(v) => uc("estado_final", v)}
                  disabled={readOnly}
                >
                  <SelectTrigger className="mt-1 h-11">
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    {ESTADO_OPS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Recepción cliente</Label>
                <Input
                  value={cierre.firma_cliente}
                  onChange={(e) => uc("firma_cliente", e.target.value)}
                  className="mt-1 h-11"
                  placeholder="Nombre de quien recibe"
                  autoComplete="name"
                  disabled={readOnly}
                />
              </div>
            </CardContent>
          </Card>
        )}

        {!necesitaTipo && step === 3 && (
          <div className="space-y-4">
            <h2 className="font-bold text-lg text-[#384091]">Revisión Final</h2>

            <Card className="border-0 shadow-sm">
              <CardContent className="p-5 space-y-2">
                {[
                  ["Folio", folio || "Pendiente"],
                  ["Cliente", datos.cliente],
                  ["Dirección", datos.direccion],
                  ["Equipo", `${datos.marca_sillon} ${datos.modelo}`.trim()],
                  ["Box", datos.numero_box],
                  ["Técnico", user?.nombre],
                ].map(([l, v]) => (
                  <div key={l} className="flex justify-between text-sm">
                    <span className="text-gray-500">{l}</span>
                    <span className="font-medium text-right max-w-[55%]">{v || "—"}</span>
                  </div>
                ))}

                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Checklist</span>
                  <Badge className={pct === 100 ? "bg-emerald-50 text-emerald-700 border-0" : "bg-amber-50 text-amber-700 border-0"}>
                    {pct}%
                  </Badge>
                </div>
              </CardContent>
            </Card>

            {ncs.length > 0 && (
              <Card className="border-red-200 bg-red-50/50 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="h-4 w-4 text-red-500" />
                    <span className="text-sm font-semibold text-red-700">
                      {ncs.length} no conforme(s)
                    </span>
                  </div>
                  {ncs.slice(0, 5).map((n) => (
                    <p key={n.id} className="text-xs text-red-600">
                      · {n.descripcion}
                    </p>
                  ))}
                </CardContent>
              </Card>
            )}

            {(() => {
              const missing = requiredMissing();
              return missing.length > 0 ? (
                <Card className="border-amber-200 bg-amber-50/50 shadow-sm">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="h-4 w-4 text-amber-600" />
                      <span className="text-sm font-semibold text-amber-700">Campos obligatorios pendientes</span>
                    </div>
                    {missing.map((m) => (
                      <p key={m} className="text-xs text-amber-700">· {m}</p>
                    ))}
                  </CardContent>
                </Card>
              ) : null;
            })()}

            <Card className="border-0 shadow-sm">
              <CardContent className="p-4">
                <p className="text-sm text-gray-500">PDF se enviará a:</p>
                <p className="text-sm font-medium">reportes@odontofix.cl</p>
                {datos.correo_cliente && (
                  <p className="text-sm font-medium">{datos.correo_cliente}</p>
                )}
              </CardContent>
            </Card>

            {folio && <div className="mb-4"><ConsumoRepuestos origenTipo="mp" origenFolio={folio} /></div>}
            <Button
              className="w-full h-12 bg-[#384091] hover:bg-[#2D3375] text-white font-medium"
              onClick={completeReport}
              disabled={completing || saving || readOnly}
            >
              {completing ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  {estado === "completado" ? "Regenerando PDF..." : "Generando PDF..."}
                </>
              ) : (
                <>
                  <Send className="h-5 w-5 mr-2" />
                  {estado === "completado" ? "Regenerar y Reenviar Reporte" : "Completar y Enviar Reporte"}
                </>
              )}
            </Button>
          </div>
        )}

        <div className="flex justify-between mt-6">
          <Button
            variant="outline"
            onClick={() => goToStep(Math.max(0, step - 1))}
            disabled={step === 0 || saving || completing}
          >
            <ArrowLeft className="h-4 w-4 mr-1" /> Anterior
          </Button>

          {step < 3 && (
            <Button
              className="bg-[#0FB4B7] text-white"
              onClick={() => goToStep(step + 1)}
              disabled={saving || completing}
            >
              Siguiente <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          )}
        </div>
      </main>
    </div>
  );
}
